import React, { useState, useCallback, useEffect } from 'react';
import { FiChevronDown, FiChevronUp, FiImage, FiEyeOff, FiEye, FiPlus, FiX } from 'react-icons/fi';
import DrawingViewer from '../../components/drawing-viewer/DrawingViewer';
import { useToast } from '../../components/toast/toastProvider';
import SiteDrawingsService from '../../services/siteDrawingsService';
import './SitesViewer.css';
import './UploadForm.css';

interface DrawingData {
  url: string;
  name: string;
  id: string;
  siteName: string;
  siteLocation: string;
}

const SitesViewer: React.FC = () => {
  const [projectId] = useState<string>('project-123');
  const [drawingId, setDrawingId] = useState<string>('drawing-001');
  const [drawingUrl, setDrawingUrl] = useState<string>('');
  const [siteName, setSiteName] = useState<string>('');
  const [siteLocation, setSiteLocation] = useState<string>('');
  const [uploadedImages, setUploadedImages] = useState<DrawingData[]>([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    siteName: '',
    siteLocation: '',
    file: null as File | null
  });
  const [dragActive, setDragActive] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // NEW: Gallery visibility state
  const [showGallery, setShowGallery] = useState<boolean>(true);
  const { addToast } = useToast();

  useEffect(() => {
    const loadDrawings = async () => {
      try {
        const drawings = await SiteDrawingsService.getDrawings();
        const mappedDrawings = drawings.map(drawing => ({
          url: drawing.file_url,
          name: drawing.file_name || drawing.title,
          id: drawing.id,
          siteName: drawing.title,
          siteLocation: drawing.description || drawing.drawing_metadata?.siteLocation || '',
        }));

        setUploadedImages(mappedDrawings);

        if (mappedDrawings.length > 0) {
          const firstDrawing = mappedDrawings[0];
          setDrawingUrl(firstDrawing.url);
          setDrawingId(firstDrawing.id);
          setSiteName(firstDrawing.siteName);
          setSiteLocation(firstDrawing.siteLocation);
        }
      } catch (error) {
        console.error('Failed to load drawings:', error);
      }
    };

    loadDrawings();
  }, []);


  // Handle form input changes
  const handleInputChange = useCallback((field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  // Handle file selection
  const handleFileSelect = useCallback((file: File) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      addToast({
        type: 'error',
        title: 'Invalid File Type',
        message: 'Please select an image file (PNG, JPG, JPEG, etc.)',
        duration: 4000
      });
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      addToast({
        type: 'error',
        title: 'File Too Large',
        message: 'Please select an image smaller than 10MB',
        duration: 4000
      });
      return;
    }

    setFormData(prev => ({
      ...prev,
      file
    }));

    // Create preview URL
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  }, [addToast]);

  // Handle drag events
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  // Handle drop
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  }, [handleFileSelect]);

  // Handle file input change
  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  }, [handleFileSelect]);

  // Handle form submission
  const handleFormSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.siteName.trim()) {
      addToast({
        type: 'error',
        title: 'Validation Error',
        message: 'Site name is required',
        duration: 3000
      });
      return;
    }

    if (!formData.siteLocation.trim()) {
      addToast({
        type: 'error',
        title: 'Validation Error',
        message: 'Site location is required',
        duration: 3000
      });
      return;
    }

    if (!formData.file) {
      addToast({
        type: 'error',
        title: 'Validation Error',
        message: 'Please select a drawing image',
        duration: 3000
      });
      return;
    }

    setUploading(true);
    
    try {
      const createdDrawing = await SiteDrawingsService.createDrawing(
        formData.siteName.trim(),
        formData.siteLocation.trim(),
        formData.file
      );

      const newDrawing: DrawingData = {
        url: createdDrawing.file_url,
        name: createdDrawing.file_name || createdDrawing.title,
        id: createdDrawing.id,
        siteName: createdDrawing.title,
        siteLocation: createdDrawing.description || createdDrawing.drawing_metadata?.siteLocation || ''
      };

      setUploadedImages(prev => [newDrawing, ...prev]);

      // Set as active drawing if it's the first one
      if (uploadedImages.length === 0) {
        setDrawingUrl(newDrawing.url);
        setDrawingId(newDrawing.id);
        setSiteName(newDrawing.siteName);
        setSiteLocation(newDrawing.siteLocation);
      }

      // Reset form
      setFormData({ siteName: '', siteLocation: '', file: null });
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
      setShowUploadModal(false);
      
      addToast({
        type: 'success',
        title: 'Drawing Uploaded',
        message: 'Drawing has been uploaded successfully.',
        duration: 4000
      });
    } catch (error) {
      console.error('Upload failed:', error);
      addToast({
        type: 'error',
        title: 'Upload Failed',
        message: 'Failed to upload drawing. Please try again.',
        duration: 4000
      });
    } finally {
      setUploading(false);
    }
  }, [formData, uploadedImages.length, addToast, previewUrl]);

  // Handle form cancel
  const handleFormCancel = useCallback(() => {
    setFormData({ siteName: '', siteLocation: '', file: null });
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setShowUploadModal(false);
  }, [previewUrl]);

  // Select an uploaded image as the active drawing
  const handleSelectImage = (imgId: string) => {
    const img = uploadedImages.find(u => u.id === imgId);
    if (img) {
      setDrawingUrl(img.url);
      setDrawingId(img.id);
      setSiteName(img.siteName);
      setSiteLocation(img.siteLocation);
    }
  };

  // Toggle gallery visibility
  const toggleGallery = () => {
    setShowGallery(prev => !prev);
  };

  return (
    <div className="sites-page">
      <header className="sites-header">
        <h1>Sites</h1>
        <p>Pin defects directly to your site drawings. Easily upload drawings from your computer.</p>

        <div className="sites-header-controls">
          <button className="sites-upload-btn" onClick={() => setShowUploadModal(true)}>
            <FiImage size={16} />
            Upload Drawing
          </button>
        </div>
      </header>

      <section className="sites-main">
        {/* Gallery Header with Toggle Button */}
        {uploadedImages.length > 0 && (
          <div className="gallery-header">
            <button 
              className="gallery-toggle-btn" 
              onClick={toggleGallery}
              title={showGallery ? "Hide gallery" : "Show gallery"}
            >
              <FiImage size={18} />
              <span>
                Drawings ({uploadedImages.length})
              </span>
              {showGallery ? (
                <>
                  <FiEyeOff size={16} />
                  <FiChevronUp size={16} />
                </>
              ) : (
                <>
                  <FiEye size={16} />
                  <FiChevronDown size={16} />
                </>
              )}
            </button>
          </div>
        )}

        {/* Collapsible Image Gallery */}
        {uploadedImages.length > 0 && (
          <div className={`sites-image-gallery-container ${showGallery ? 'expanded' : 'collapsed'}`}>
            <div className="sites-image-gallery">
              {uploadedImages.map(img => (
                <div
                  key={img.id}
                  className={`gallery-item${drawingId === img.id ? ' active' : ''}`}
                  onClick={() => handleSelectImage(img.id)}
                >
                  <img src={img.url} alt={img.name} />
                  <span className="image-name" title={img.name}>
                    {img.name.length > 15 ? `${img.name.substring(0, 15)}...` : img.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Drawing viewer */}
        {drawingUrl ? (
          <DrawingViewer
            drawingUrl={drawingUrl}
            drawingId={drawingId}
            projectId={projectId}
            siteName={siteName}
            siteLocation={siteLocation}
            className="sites-drawing-viewer"
            style={{ 
              height: showGallery && uploadedImages.length > 0 ? '65vh' : '75vh', 
              minHeight: 400 
            }}
          />
        ) : (
          <div className="sites-placeholder">
            <FiImage size={64} color="#8b5cf6" />
            <h2>No drawing selected</h2>
            <p>Upload an image to start pinning defects.</p>
            <button 
              className="placeholder-upload-btn" 
              onClick={() => setShowUploadModal(true)}
            >
              <FiImage size={16} />
              Upload First Drawing
            </button>
          </div>
        )}
      </section>

      {/* Drawing upload modal */}
      {showUploadModal && (
        <div className="sites-upload-modal-overlay">
          <div className="sites-upload-modal">
            <div className="sites-upload-modal-header">
              <h3>Upload Drawing</h3>
              <button
                type="button"
                className="close-btn"
                onClick={handleFormCancel}
                disabled={uploading}
              >
                <FiX size={20} />
              </button>
            </div>

            <form onSubmit={handleFormSubmit} className="sites-upload-modal-body">
              {/* Site Information */}
              <div className="form-section">
                <h4>Site Information</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="site-name">
                      Site Name *
                    </label>
                    <input
                      id="site-name"
                      type="text"
                      value={formData.siteName}
                      onChange={(e) => handleInputChange('siteName', e.target.value)}
                      placeholder="Enter site name"
                      required
                      disabled={uploading}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="site-location">
                      Site Location *
                    </label>
                    <input
                      id="site-location"
                      type="text"
                      value={formData.siteLocation}
                      onChange={(e) => handleInputChange('siteLocation', e.target.value)}
                      placeholder="Enter site location"
                      required
                      disabled={uploading}
                    />
                  </div>
                </div>
              </div>

              {/* File Upload */}
              <div className="form-section">
                <h4>Drawing Image</h4>
                <div
                  className={`file-upload-area ${dragActive ? 'drag-active' : ''} ${formData.file ? 'has-file' : ''}`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
            <input
              type="file"
              accept="image/*"
                    onChange={handleFileInputChange}
              className="file-input"
                    disabled={uploading}
                  />
                  
                  {formData.file ? (
                    <div className="file-preview">
                      {previewUrl && (
                        <img
                          src={previewUrl}
                          alt="Drawing preview"
                          className="preview-image"
                        />
                      )}
                      <div className="file-info">
                        <div className="file-name">
                          <FiImage size={16} />
                          <span>{formData.file.name}</span>
                        </div>
                        <div className="file-size">
                          {(formData.file.size / (1024 * 1024)).toFixed(2)} MB
                        </div>
                      </div>
                      <button
                        type="button"
                        className="remove-file-btn"
                        onClick={() => {
                          setFormData(prev => ({ ...prev, file: null }));
                          if (previewUrl) {
                            URL.revokeObjectURL(previewUrl);
                            setPreviewUrl(null);
                          }
                        }}
                        disabled={uploading}
                      >
                        <FiX size={16} />
                      </button>
                    </div>
                  ) : (
                    <div className="file-upload-placeholder">
                      <FiImage size={48} />
                      <h4>Drop your drawing here</h4>
                      <p>or <button type="button" onClick={() => {
                        const input = document.querySelector('.file-input') as HTMLInputElement;
                        input?.click();
                      }}>browse files</button></p>
                      <div className="file-requirements">
                        <p>Supported formats: PNG, JPG, JPEG, GIF</p>
                        <p>Maximum size: 10MB</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Form Actions */}
              <div className="form-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={handleFormCancel}
                  disabled={uploading}
                >
                Cancel
              </button>
                <button
                  type="submit"
                  className="proceed-btn"
                  disabled={uploading || !formData.file || !formData.siteName.trim() || !formData.siteLocation.trim()}
                >
                  {uploading ? (
                    <>
                      <div className="loading-spinner"></div>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <FiPlus size={16} />
                      Proceed
                    </>
                  )}
                </button>
            </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SitesViewer;